/**
 * Authentication Service
 *
 * Handles user authentication operations:
 * - Register
 * - Login
 * - Logout
 * - Token verification
 * - Get current user
 */

import { apiClient, type ApiResponse } from './apiClient';

export interface User {
  id: number;
  email: string;
  status: 'registered' | 'pending_approval' | 'approved_pending_payment' | 'active' | 'rejected' | 'inactive';
  hubspot_contact_id: string | null;
  rejection_reason: string | null;
  payment_date: string | null;
  subscription_end_date: string | null;
  created_at: string;
  updated_at: string;
  last_login: string | null;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  token: string;
  user: User;
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  user: User;
}

export interface VerifyResponse {
  success: boolean;
  user: User;
  session: {
    expires_at: string;
    last_activity: string;
  };
}

export interface MeResponse {
  success: boolean;
  user: User;
}

class AuthService {
  /**
   * Register new user
   */
  async register(email: string, password: string): Promise<RegisterResponse> {
    const response = await apiClient.post<RegisterResponse>('/auth/register', {
      email,
      password,
    });

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Error al registrar usuario');
    }

    return response.data;
  }

  /**
   * Login user
   */
  async login(email: string, password: string, deviceName?: string): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>('/auth/login', {
      email,
      password,
      device_name: deviceName || 'Web Browser',
    });
    console.log(response);
    if (!response.success || !response.data) {
      throw new Error(response.error || 'Error al iniciar sesión');
    }

    // Save token to localStorage
    if (response.data.token) {
      apiClient.setToken(response.data.token);
    }

    return response.data;
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout', {}, true);
    } catch (error) {
      // Logout is idempotent, so we don't throw errors
      console.error('Logout error:', error);
    } finally {
      // Always clear token, even if request fails
      apiClient.clearToken();
    }
  }

  /**
   * Verify current token
   */
  async verifyToken(): Promise<VerifyResponse> {
    const response = await apiClient.get<VerifyResponse>('/auth/verify', true);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Token inválido');
    }

    return response.data;
  }

  /**
   * Get current user data
   */
  async getMe(): Promise<User> {
    const response = await apiClient.get<MeResponse>('/auth/me', true);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Error al obtener datos de usuario');
    }

    return response.data.user;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return apiClient.isAuthenticated();
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return apiClient.getToken();
  }

  /**
   * Clear authentication
   */
  clearAuth(): void {
    apiClient.clearToken();
  }
}

// Export singleton instance
export const authService = new AuthService();
